import React, { useState, useContext, useCallback } from 'react';
import { TextField, Grid, Container, IconButton, InputAdornment, Backdrop, CircularProgress, Button, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import { AuthContext } from '../../../contexts/auth';
import SearchButton from '../../../components/buttons/search';
import InvoiceAccordion from '../../../components/displays/invoiceAccordion';

const HOST = process.env.REACT_APP_HOST;

const formatCNPJ = (value) => {
  value = value.replace(/\D/g, '');
  const cnpjPattern = /(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/;
  value = value.replace(cnpjPattern, (_, p1, p2, p3, p4, p5) => {
    let formattedValue = '';
    if (p1) formattedValue += p1;
    if (p2) formattedValue += '.' + p2;
    if (p3) formattedValue += '.' + p3;
    if (p4) formattedValue += '/' + p4;
    if (p5) formattedValue += '-' + p5;
    return formattedValue;
  });
  return value.slice(0, 18);
};

const formatTelefone = (num) => {
  const telPattern = /(\d{0,2})(\d{0,1})(\d{0,8})/;
  return num.replace(telPattern, (match, p1, p2, p3) => {
    let formattedValue = '';
    if (p1) formattedValue += p1;
    if (p2) formattedValue += ' ' + p2;
    if (p3) formattedValue += ' ' + p3;
    return formattedValue;
  });
};

function SearchContratante() {
  const { token, loading, toggleLoading } = useContext(AuthContext);

  const [data, setData] = useState({
    razao_social: '',
    cnpj: '',
    e_mail: '',
    endereco: '',
    cep: '',
    cidade: '',
    estado: '',
    bairro: '',
    num: ''
  });

  // ----- Estados de Faturas -----
  const [mes, setMonth] = useState(1);
  const [ano, setYear] = useState(2026);
  const [fats, setFaturas] = useState([]);

  // ----- Buscar Contratante -----
  const handleSearchCNPJ = useCallback(async () => {
    toggleLoading(true);
    try {
      const apiUrl = `${HOST}/api/admin/searchContratante`;
      const response = await axios.get(apiUrl, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
       },
        params: { cnpj: data.cnpj }
      });

      if (response.status === 200) {
        setData(prevData => ({
          ...prevData,
          ...response.data.contratante,
          num: formatTelefone(response.data.contratante.num)
        }));
      } else {
        alert('Erro ao buscar dados');
      }
    } catch (error) {
      alert('Erro ao buscar dados');
      console.error(`Erro ao buscar dados pelo CNPJ`, error);
    } finally {
      toggleLoading(false);
    }
  }, [data.cnpj, toggleLoading]);

  // ----- Atualizar Contratante -----
  const updateData = async () => {
    toggleLoading(true);
    try {
      const apiUrl = `${HOST}/api/admin/contratante`;
      const dataSend = {
        cnpj: data.cnpj.replace(/\s+/g, ''),
        num: data.num.replace(/\s+/g, ''),
        e_mail: data.e_mail
      };
      const response = await axios.put(apiUrl, dataSend, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
  
      toggleLoading(false);
      if (response.status === 200) {
        alert('Dados atualizados com sucesso!');
      } else {
        alert('Erro ao atualizar dados');
      }
    } catch (error) {
      toggleLoading(false);
      alert('Erro ao atualizar dados');
      console.error("Erro ao atualizar os dados do beneficiário", error);
    }
  };

  // ----- Buscar Faturas -----
  const handleSearchFaturas = () => {
    toggleLoading(true);
    const competencia = `${mes}-${ano}`;

    fetch(`${HOST}/api/admin/fat/${competencia}/${data.cnpj.replace(/\D/g, '')}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      }
    })
      .then(response => {
        toggleLoading(false);
        if (!response.ok) {
          setFaturas([]);
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setFaturas(data);
      })
      .catch(error => {
        toggleLoading(false);
        console.error('Erro ao buscar as faturas:', error);
      });
  };

  // ----- Handlers -----
  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name !== "e_mail") {
      value = value.replace(/\D/g, '');
    }

    if (name === "cnpj") {
      value = formatCNPJ(value);
      if (value.length > 18) {
        value = value.slice(0, 18);
      }
    } else if (name === "num") {
      value = formatTelefone(value);
      if (value.length > 13) {
        value = value.slice(0, 13);
      }
    }

    setData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const renderTextField = (label, name, isEditable = false) => (
    <TextField
      fullWidth
      label={label}
      name={name}
      value={data[name]}
      onChange={handleChange}
      InputProps={{ readOnly: !isEditable }}
    />
  );

  return (
    <Container>
      {/* --- FORM CONTRATANTE --- */}
      <h3>Atualização de dados</h3>
      <Grid container spacing={3} alignItems="center" justifyContent="center">
        <Grid item xs={6}>{renderTextField("Razão Social", "razao_social")}</Grid>
        <Grid key={'cnpj'} item xs={6}>
          <TextField
            fullWidth
            label={'CNPJ'}
            name={'cnpj'}
            value={data['cnpj']}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearchCNPJ}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>{renderTextField("Telefone", "num", true)}</Grid>
        <Grid item xs={6}>{renderTextField("E-Mail", "e_mail", true)}</Grid>
        <Grid item xs={6}>{renderTextField("Endereço", "endereco")}</Grid>
        <Grid item xs={6}>{renderTextField("Bairro", "bairro")}</Grid>
        <Grid item xs={5}>{renderTextField("Cidade", "cidade")}</Grid>
        <Grid item xs={5}>{renderTextField("CEP", "cep")}</Grid>
        <Grid item xs={2}>{renderTextField("Estado", "estado")}</Grid>
        <Grid item xs={12} container justifyContent="center">
          <Button 
            onClick={updateData} 
            endIcon={<UpdateIcon />}
            sx={{ bgcolor: '#008b50', color: '#fafafa', marginTop: 2, '&:hover': { bgcolor: '#006b40' }}}
            disabled={!data.cnpj || data.cnpj.length < 18 || !data.e_mail}
          >
            Atualizar
          </Button>
        </Grid>
      </Grid>

      {/* --- ÁREA DE FATURAS --- */}
      <div style={{ marginTop: '40px' }}>
        <h3>Faturas</h3>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" marginBottom={3}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Mês</InputLabel>
            <Select value={mes} onChange={(e) => setMonth(e.target.value)}>
              <MenuItem value={1}>Janeiro</MenuItem>
              <MenuItem value={2}>Fevereiro</MenuItem>
              <MenuItem value={3}>Março</MenuItem>
              <MenuItem value={4}>Abril</MenuItem>
              <MenuItem value={5}>Maio</MenuItem>
              <MenuItem value={6}>Junho</MenuItem>
              <MenuItem value={7}>Julho</MenuItem>
              <MenuItem value={8}>Agosto</MenuItem>
              <MenuItem value={9}>Setembro</MenuItem>
              <MenuItem value={10}>Outubro</MenuItem>
              <MenuItem value={11}>Novembro</MenuItem>
              <MenuItem value={12}>Dezembro</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel>Ano</InputLabel>
            <Select value={ano} onChange={(e) => setYear(e.target.value)}>
              {/* <MenuItem value={2021}>2021</MenuItem>
              <MenuItem value={2022}>2022</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
              <MenuItem value={2024}>2024</MenuItem> */}
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2026}>2026</MenuItem>
            </Select>
          </FormControl>
          <SearchButton onSearchClick={handleSearchFaturas} />
        </Stack>

        <InvoiceAccordion faturas={fats} />
      </div>

      {/* --- Loading --- */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}

export default SearchContratante;
